import { ParameterApi } from '@app/api';
import { ChangeAuthor } from '@app/components/ChangeAuthor';
import { DataLoadError } from '@app/components/DataLoadError';
import { Parameter, ParameterChange } from '@app/model/Parameter';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { ddMMyyyyHHmmFormat } from '@app/utils/functions';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner
} from '@patternfly/react-core';
import { HistoryIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

export interface ParameterHistoryModalProps {
  /** The parameter whose history to show. Null closes the modal. */
  parameter: Parameter | null;
  onClose(): void;
}

/** ADD, MOD and DEL as an operator would describe them. */
const CHANGE_LABELS: Record<ParameterChange['type'], { text: string; color: 'blue' | 'orange' | 'grey' }> = {
  ADD: { text: 'Set', color: 'blue' },
  MOD: { text: 'Changed', color: 'orange' },
  DEL: { text: 'Reset', color: 'grey' }
};

/**
 * Every recorded change to one parameter. Read on open rather than with the list, because it is
 * one request per parameter and an operator asks for it about one setting at a time.
 *
 * The history spans every incarnation of a key: a reset deletes the override row and setting the
 * value again inserts a new one, so a setting that has been reset and set again shows the whole
 * sequence rather than starting over.
 */
export const ParameterHistoryModal: React.FunctionComponent<ParameterHistoryModalProps> = ({
  parameter,
  onClose
}) => {
  const [changes, setChanges] = React.useState<ParameterChange[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<ApiError | null>(null);

  const key = parameter?.name;

  const load = React.useCallback(async () => {
    if (!key) return;

    setIsLoading(true);
    setError(null);
    try {
      setChanges(await ParameterApi.history(key));
    } catch (err) {
      setError(toApiError(err));
      setChanges([]);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <Modal
      variant={ModalVariant.medium}
      isOpen={!!parameter}
      onClose={onClose}
      aria-label="Parameter change history"
    >
      <ModalHeader title={`History — ${parameter?.property ?? ''}`} titleIconVariant={HistoryIcon} />
      <ModalBody>
        {isLoading && (
          <Bullseye>
            <Spinner aria-label="Loading history" />
          </Bullseye>
        )}

        {!isLoading && error && (
          <DataLoadError error={error} entityLabel="the change history" onRetry={() => void load()} />
        )}

        {!isLoading && !error && changes.length === 0 && (
          <Bullseye>
            <EmptyState headingLevel="h4" icon={HistoryIcon} titleText="Never changed">
              <EmptyStateBody>
                This setting has no recorded changes, so it is still on the value the service was
                deployed with.
              </EmptyStateBody>
            </EmptyState>
          </Bullseye>
        )}

        {!isLoading && !error && changes.length > 0 && (
          <Table aria-label="Parameter change history" variant="compact">
            <Thead>
              <Tr>
                {/* Not narrower than this: at width 10 PatternFly ellipsises the header itself,
                    so the column reads "Revis…" before any data is even in it. */}
                <Th width={15}>Revision</Th>
                <Th width={20}>When</Th>
                <Th width={20}>Who</Th>
                <Th width={15}>Change</Th>
                <Th>Value</Th>
              </Tr>
            </Thead>
            <Tbody>
              {changes.map(change => {
                const label = CHANGE_LABELS[change.type];
                return (
                  <Tr key={`${change.revision}-${change.type}`}>
                    <Td dataLabel="Revision">{change.revision}</Td>
                    <Td dataLabel="When">{ddMMyyyyHHmmFormat(change.changedAt)}</Td>
                    <Td dataLabel="Who"><ChangeAuthor username={change.username} /></Td>
                    <Td dataLabel="Change">
                      <Label isCompact color={label?.color ?? 'grey'}>{label?.text ?? change.type}</Label>
                    </Td>
                    <Td dataLabel="Value" modifier="breakWord">
                      {change.value == null ? (
                        <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
                          override removed
                        </span>
                      ) : (
                        <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{change.value}</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};
